import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Shield, ArrowLeft, Mail, Lock, Check, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import LoginLeftSide from '../components/LoginLeftSide';
import { apiRequest } from '../utils/api';

// Styled Components
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  @media (min-width: 1024px) {
    flex-direction: row;
  }
`;

const RightSection = styled.div`
  width: 100%;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  margin-left: 0;
  min-height: 100vh;

  @media (min-width: 1024px) {
    width: 50%;
    margin-left: 50%;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const FormWrapper = styled.div`
  width: 100%;
  max-width: 480px;
  padding: 1rem;

  @media (max-width: 480px) {
    padding: 0;
  }
`;

const FormHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const FormTitle = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 1rem;

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const FormSubtitle = styled.p`
  color: #6b7280;
  font-size: 1rem;

  @media (max-width: 480px) {
    font-size: 0.875rem;
  }
`;

const Card = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  z-index: 1;
  pointer-events: none;
`;

const InputField = styled.input`
  width: 100%;
  padding: 1rem 1rem 1rem 3rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: transparent;
    box-shadow: 0 0 0 3px rgba(21, 98, 183, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 0.75rem 0.75rem 3rem;
    font-size: 0.875rem;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background-color: #1562B7;
  color: #fff;
  padding: 1rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0f529c;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    font-size: 0.875rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #1562B7;
  text-decoration: none;
  font-size: 0.875rem;

  &:hover {
    color: #0f529c;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const Message = styled.div`
  padding: 1rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;

  &.error {
    background: #fee2e2;
    color: #dc2626;
  }

  &.success {
    background: #dcfce7;
    color: #059669;
  }
`;

const ModalOverlay = styled.div`
  display: ${(props) => (props.show ? 'flex' : 'none')};
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #fff;
  padding: 2rem;
  border-radius: 0.75rem;
  width: 90%;
  max-width: 600px;

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const PasswordRequirement = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${(props) => (props.met ? '#1562B7' : '#dc2626')};
  margin: 0.25rem 0;
`;

const RequirementsList = styled.div`
  margin: 0.5rem 0 1rem;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 0.5rem;

  @media (max-width: 480px) {
    padding: 0.5rem;
  }
`;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });

  useEffect(() => {
    setPasswordRequirements({
      length: newPassword.length >= 8,
      uppercase: /[A-Z]/.test(newPassword),
      lowercase: /[a-z]/.test(newPassword),
      number: /[0-9]/.test(newPassword),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
    });
  }, [newPassword]);

  const areAllRequirementsMet = Object.values(passwordRequirements).every(Boolean);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const response = await apiRequest('/api/auth/reset-password/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (!response.ok) {
        setErrorMessage(data.message || 'Failed to request password reset.');
      } else {
        setSuccessMessage(data.message);
        setIsOtpModalOpen(true);
      }
    } catch (err) {
      setErrorMessage('Server error. Please try again later.');
    }
    setIsSubmitting(false);
  };

  const handleConfirmReset = async () => {
    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    if (!otp || !newPassword || !confirmPassword) {
      setErrorMessage('Please fill in all fields.');
      setIsSubmitting(false);
      return;
    }
    if (!areAllRequirementsMet) {
      setErrorMessage('Please ensure all password requirements are met.');
      setIsSubmitting(false);
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage('New passwords do not match.');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await apiRequest('/api/auth/reset-password/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp, newPassword }),
      });
      const data = await response.json();
      if (!response.ok) {
        setErrorMessage(data.message || 'Failed to reset password.');
      } else {
        setSuccessMessage('Password reset successfully. Redirecting to login...');
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (err) {
      setErrorMessage('Server error. Please try again later.');
    }
    setIsSubmitting(false);
  };

  const RequirementItem = ({ met, text }) => (
    <PasswordRequirement met={met}>
      {met ? <Check size={16} /> : <X size={16} />} {text}
    </PasswordRequirement>
  );

  return (
    <PageContainer>
      <LoginLeftSide />
      <RightSection>
        <FormWrapper>
          <FormHeader>
            <FormTitle>Password Reset</FormTitle>
            <FormSubtitle>Enter your email and we'll send a code to reset your password.</FormSubtitle>
          </FormHeader>

          <Card>
            {successMessage && <Message className="success">{successMessage}</Message>}
            {errorMessage && <Message className="error">{errorMessage}</Message>}

            <Form onSubmit={handleRequestReset}>
              <div>
                <Label htmlFor="email">Email</Label>
                <InputWrapper>
                  <InputIcon><Mail size={20} /></InputIcon>
                  <InputField
                    id="email"
                    type="email"
                    placeholder="name@company.com"
                    required
                    hasIcon
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </InputWrapper>
              </div>
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Code'}
              </SubmitButton>
              <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                <BackLink to="/login">
                  <ArrowLeft size={16} /> Back
                </BackLink>
              </div>
            </Form>
          </Card>
        </FormWrapper>
      </RightSection>

      <ModalOverlay show={isOtpModalOpen} onClick={() => setIsOtpModalOpen(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <h3 style={{ marginBottom: '1rem' }}>Reset Your Password</h3>
          {errorMessage && <Message className="error">{errorMessage}</Message>}
          {successMessage && <Message className="success">{successMessage}</Message>}

          <Label>OTP Code</Label>
          <InputField
            type="text"
            placeholder="Enter 6-digit code"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
          />

          <Label>New Password</Label>
          <InputWrapper>
            <InputIcon><Lock size={20} /></InputIcon>
            <InputField
              type="password"
              placeholder="Enter new password"
              hasIcon
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </InputWrapper>

          <RequirementsList>
            <RequirementItem met={passwordRequirements.length} text="At least 8 characters" />
            <RequirementItem met={passwordRequirements.uppercase} text="One uppercase letter" />
            <RequirementItem met={passwordRequirements.lowercase} text="One lowercase letter" />
            <RequirementItem met={passwordRequirements.number} text="One number" />
            <RequirementItem met={passwordRequirements.special} text="One special character" />
          </RequirementsList>

          <Label>Confirm New Password</Label>
          <InputWrapper>
            <InputIcon><Lock size={20} /></InputIcon>
            <InputField
              type="password"
              placeholder="Confirm new password"
              hasIcon
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </InputWrapper>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1.5rem' }}>
            <SubmitButton
              type="button"
              onClick={handleConfirmReset}
              disabled={isSubmitting || !areAllRequirementsMet || !confirmPassword}
            >
              {isSubmitting ? 'Submitting...' : 'Reset Password'}
            </SubmitButton>
            <SubmitButton type="button" style={{ background: '#999' }} onClick={() => setIsOtpModalOpen(false)}>
              Cancel
            </SubmitButton>
          </div>
        </ModalContent>
      </ModalOverlay>
    </PageContainer>
  );
};

export default ForgotPasswordPage;
